import { ConfigFeature } from '../../../../features/config/contract'
import { sendBaseCommand } from '../../protocols/base-command/command-io'
import type { BaseDockProfile } from '../../capabilities/base-command/profile'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import type { Config } from '../../../../types/config'

const BASE_DEFAULT_CONFIG: Config = { index: 0, name: '默认配置' }

/**
 * 底座配置实现。
 *
 * 底座没有多配置槽位，这里只承接与键盘/鼠标统一的 `features.config` 入口，
 * 目前主要用于恢复出厂（`initAllConfigs`）。
 */
export class RapooBaseConfig extends ConfigFeature {
  constructor(
    device: BaseDevice,
    private readonly profile: BaseDockProfile,
  ) {
    super(device)
  }

  /**
   * 底座页面会统一读取配置列表；P5/P6 未确认多配置协议，固定返回唯一默认项。
   * 这避免把原厂 `BaseStation` 继承来的鼠标 EEPROM 逻辑误用到 Telink 底座。
   */
  public async getAllConfigs(): Promise<Config[]> {
    this.syncDefaultConfig()
    return this.device.configList
  }

  /** 读取底座唯一的当前配置。 */
  public async getCurrentConfig(): Promise<Config> {
    this.syncDefaultConfig()
    return this.device.currentConfig
  }

  /** 底座固定使用配置槽 0。 */
  public async getCurrentConfigIndex(): Promise<number> {
    return BASE_DEFAULT_CONFIG.index
  }

  /** 底座没有可改名的配置槽，只暴露默认名称。 */
  public async getCurrentConfigName(index = BASE_DEFAULT_CONFIG.index): Promise<string> {
    this.assertDefaultConfigIndex(index)
    return BASE_DEFAULT_CONFIG.name
  }

  /** 允许页面重复选择默认项，拒绝不存在的配置槽。 */
  public async setCurrentConfig(index: number): Promise<void> {
    this.assertDefaultConfigIndex(index)
    this.syncDefaultConfig()
  }

  /** 恢复底座出厂设置。与键盘/鼠标统一走 `features.config.initAllConfigs()`。 */
  async initAllConfigs(): Promise<void> {
    if (this.profile.name === 'VT_nrf54L_Base') {
      if (this.device.manifest.baseDisplay === 'screen') {
        // VT 底座恢复出厂前先重置屏幕芯片，再发 0xad + 0x52 0x3d 确认参数。
        await this.features.baseScreen?.resetScreenChip()
      }
      await sendBaseCommand(
        this.device,
        this.profile.config,
        this.commands.return_factory_settings,
        [0x52, 0x3d],
      )
    } else {
      // Telink（P5/P6）恢复出厂命令为 0xb2。
      await sendBaseCommand(this.device, this.profile.config, this.commands.return_factory_settings)
    }

    this.device.cache.clearAll()
    this.syncDefaultConfig()
  }

  /** 底座无管理员出厂配置槽，行为与 initAllConfigs 一致。 */
  async initAllConfigsByAdmin(): Promise<void> {
    await this.initAllConfigs()
  }

  private get commands() {
    return this.profile.commands
  }

  private syncDefaultConfig(): void {
    this.device.currentConfig = { ...BASE_DEFAULT_CONFIG }
    this.device.configList = [{ ...BASE_DEFAULT_CONFIG }]
  }

  private assertDefaultConfigIndex(index: number): void {
    if (index !== BASE_DEFAULT_CONFIG.index) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '底座仅支持默认配置槽 0')
    }
  }
}
